<template>
	<div>
		<div class="block w-full">
			<div class="grid grid-cols-1 gap-4 w-full mb-5">
				<div>
					<div class="inputTitle font-semibold mb-1">
						{{ $t('popups.settings.systemSettings.name') }}
					</div>
					<div class="inputField flex-grow mb-4">
						<input type="text" v-model="popupData.name" disabled class="w-full p-2 border border-gray-200 bg-gray-50 rounded" />
					</div>
					
					<div class="inputTitle font-semibold mb-1">
						{{ $t('popups.settings.systemSettings.value') }}
					</div>
					<div class="inputField flex-grow">
						
						<!-- Dropdown for PossibleValues -->
						<template v-if="hasPossibleValues">
							<Select 
								:options="selectOptions"
								:selected="popupData.value"
								:searchable="true"
								mode="single"
								@selectChanged="onSelectChanged"
							/>
						</template>
						
						<!-- Passwörter -->
						<template v-else-if="popupData.dataControlJavaScript === 'Passwort'">
							<input type="password" v-model="popupData.value" class="w-full p-2 border border-gray-300 rounded" />
						</template>
						
						<!-- MUSTACHE TEMPLATE (Highlighting & viel Platz) -->
						<template v-else-if="popupData.dataControlJavaScript === 'MustacheTemplate'">
							<div class="relative w-full border border-gray-300 rounded overflow-hidden bg-white" style="height: 450px;">
								<div 
									ref="highlightLayer" 
									class="absolute inset-0 w-full h-full overflow-hidden p-4 font-mono text-sm whitespace-pre-wrap break-words leading-relaxed" 
									v-html="formattedMustache">
								</div>
								
								<textarea 
									@scroll="syncScroll" 
									v-model="popupData.value" 
									class="absolute inset-0 w-full h-full p-4 font-mono text-sm leading-relaxed transparent-text caret-black outline-none resize-none whitespace-pre-wrap break-words z-10" 
									spellcheck="false">
								</textarea>
							</div>
							<div class="mt-2 text-xs text-gray-500 flex items-center">
								<span class="inline-block w-3 h-3 bg-blue-100 border border-blue-300 mr-2 rounded"></span>
								<span>Mustache Variablen werden automatisch blau hervorgehoben (`{{ variable }}`).</span>
							</div>
						</template>
						
						<!-- HTML / MultiLine Text -->
						<template v-else-if="popupData.dataControlJavaScript === 'HTML' || popupData.dataControlJavaScript === 'MultiLine'">
							<textarea v-model="popupData.value" class="w-full p-3 font-mono text-sm border border-gray-300 rounded leading-relaxed" rows="16"></textarea>
						</template>
						
						<!-- Email Liste / Einzel E-Mail -->
						<template v-else-if="popupData.dataControlJavaScript === 'EMailList' || popupData.dataControlJavaScript === 'EMail'">
							<input type="text" v-model="popupData.value" class="w-full p-2 border border-gray-300 rounded" @blur="validateEmail" @input="validateEmail" />
							<div v-if="emailError" class="text-red-500 text-sm mt-1 font-semibold">{{ emailError }}</div>
						</template>
						
						<!-- Link / Url -->
						<template v-else-if="popupData.dataControlJavaScript === 'Url'">
							<input type="url" v-model="popupData.value" class="w-full p-2 border border-gray-300 rounded" />
						</template>
						
						<!-- Boolean Checkbox -->
						<template v-else-if="isBoolean">
							<label class="flex items-center cursor-pointer mt-2 p-2 border border-gray-200 rounded hover:bg-gray-50 transition w-max">
								<input type="checkbox" v-model="booleanProxy" class="h-6 w-6 text-blue-600 rounded" />
								<span class="ml-3 font-medium text-gray-700">{{ booleanProxy ? $t('global.active') : $t('global.inactive') }}</span>
							</label>
						</template>

						<!-- Integers & Doubles -->
						<template v-else-if="isNumber">
							<input type="number" step="any" v-model="popupData.value" class="w-full p-2 border border-gray-300 rounded" />
						</template>

						<!-- Standard Text / Fallback -->
						<template v-else>
							<div v-if="popupData.value && popupData.value.length > stringLengthForTextarea">
								<textarea v-model="popupData.value" class="w-full p-2 border border-gray-300 rounded" rows="5"></textarea>
							</div>
							<div v-else>
								<input type="text" v-model="popupData.value" class="w-full p-2 border border-gray-300 rounded" />
							</div>
						</template>
					</div>

					<div v-html="popupData.helpText" class="helpText mt-4 text-sm p-3 bg-blue-50 text-blue-800 rounded border border-blue-100"></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import {defineProps, ref, toRef, computed} from "vue";
import Select from "@/components/inputs/Select.vue";
import {useI18n} from "vue-i18n";

const { t } = useI18n();
const stringLengthForTextarea = 100;
const props = defineProps({
	data: Object
})

const popupData = toRef(props, 'data');
const emailError = ref("");
const highlightLayer = ref(null);

const validateEmail = () => {
    emailError.value = "";
    if (!popupData.value.value) return;
    
    // Sehr tolerante Prüfung, erlaubt Komma- oder Semikolon-getrennte Listen
    const emails = popupData.value.value.split(/[,;]/).map(e => e.trim()).filter(e => e.length > 0);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
        emailError.value = `Ungültige E-Mail-Adresse(n): ${invalidEmails.join(', ')}`;
    }
};

const syncScroll = (e) => {
    if (highlightLayer.value) {
        highlightLayer.value.scrollTop = e.target.scrollTop;
        highlightLayer.value.scrollLeft = e.target.scrollLeft;
    }
};

const formattedMustache = computed(() => {
    let text = popupData.value.value || "";
    
    // XSS & Markup Break Prevention
    text = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    
    // Template Pattern für Mustache: highlight the whole pill
    text = text.replace(/(\{\{.*?\}\})/g, '<span class="bg-blue-100 text-blue-800 px-1 py-0.5 rounded shadow-sm font-bold border border-blue-200">$1</span>');
    
    // Prevent collapsing empty lines at the end inside the div
    if (text.endsWith('\n')) {
        text += ' ';
    }
    
    // Note: CSS pre-wrap takes care of rendering multiple spaces and newlines natively.
    return text;
});

const hasPossibleValues = computed(() => {
	return (popupData.value.possibleValues && popupData.value.possibleValues.length > 0) || 
		(popupData.value.possibleValuesJson && popupData.value.possibleValuesJson.length > 0);
});

const selectOptions = computed(() => {
	if (!hasPossibleValues.value) return [];
	
	let values = popupData.value.possibleValues;
	
	// Fallback to parsing possibleValuesJson if possibleValues is empty
	if ((!values || values.length === 0) && popupData.value.possibleValuesJson) {
		try {
			values = JSON.parse(popupData.value.possibleValuesJson);
		} catch (e) {
			console.error("Error parsing possibleValuesJson", e);
			return [];
		}
	}

	if (!Array.isArray(values)) return [];

	return values.map(v => {
		let item = v;
		
		// If v is a string that looks like a JSON object, try to parse it
		if (typeof v === 'string' && v.trim().startsWith('{')) {
			try {
				item = JSON.parse(v);
			} catch (e) {
				// Not valid JSON, keep as string
			}
		}

		if (typeof item === 'string') {
			return { id: item, name: t(item) };
		}
		
		// Handle both id/name and Id/Name (backend often uses PascalCase)
		// Also handle other common variants like Text or value
		const id = item.id !== undefined ? item.id : (item.Id !== undefined ? item.Id : item.ID);
		const name = item.name !== undefined ? item.name : (item.Name !== undefined ? item.Name : (item.Text !== undefined ? item.Text : id));
		
		return { 
			id: id !== undefined ? String(id) : '', 
			name: name !== undefined ? t(String(name)) : String(id || '') 
		};
	});
});

const isBoolean = computed(() => {
	return popupData.value.dataType === 'System.Boolean';
});

const isNumber = computed(() => {
	return popupData.value.dataType === 'System.Int32' || 
		popupData.value.dataType === 'System.Int64' || 
		popupData.value.dataType === 'System.Double' ||
		popupData.value.dataType === 'System.Decimal';
});

const booleanProxy = computed({
	get: () => {
		if (popupData.value.value === null || popupData.value.value === undefined) return false;
		return String(popupData.value.value).trim().toLowerCase() === 'true';
	},
	set: (val) => {
		popupData.value.value = val ? 'True' : 'False';
	}
});

const onSelectChanged = (val) => {
	popupData.value.value = val;
};
</script>

<style scoped>
.caret-black {
    caret-color: #0f172a; /* Tailwind slate-900 */
}
.transparent-text {
    color: transparent !important;
    background-color: transparent !important;
}
/* For better selection visual behavior on Chrome/Firefox using transparent text */
.transparent-text::selection {
    background-color: rgba(59, 130, 246, 0.2);
    color: transparent;
}
</style>
